import { DateValue } from '@affine/core/components/workspace-property-types/date';
import {
  dateValueToNormalized,
  normalizedToDateValue,
} from '@affine/core/components/workspace-property-types/date-value';
import { normalizeDate } from '@blocksuite/data-view/property-presets';
import type { LiveData } from '@toeverything/infra';
import { useLiveData } from '@toeverything/infra';

import type { DatabaseCellRendererProps } from '../../../types';

type RawDateValue =
  | number
  | string
  | { start: number; end?: number; hasTime?: boolean }
  | undefined
  | null;

/**
 * Database date cells hold either a bare timestamp (legacy) or a
 * `{ start, end?, hasTime? }` range. Both directions go through the string
 * codec bridge so a range or a time edited here survives the write; a plain
 * timestamp stays a plain timestamp.
 */
export const DateCell = ({
  cell,
  rowId,
  dataSource,
  onChange,
}: DatabaseCellRendererProps) => {
  const value = useLiveData(cell.value$ as LiveData<RawDateValue>);
  const normalized = normalizeDate(value ?? null);

  return (
    <DateValue
      value={normalized ? normalizedToDateValue(normalized) : ''}
      onChange={v => {
        const parsed = v ? dateValueToNormalized(v) : undefined;
        if (!parsed) {
          dataSource.cellValueChange(rowId, cell.property.id, null);
          onChange?.(null);
          return;
        }
        const next =
          parsed.end != null || parsed.hasTime
            ? {
                start: parsed.start,
                ...(parsed.end != null ? { end: parsed.end } : {}),
                ...(parsed.hasTime ? { hasTime: true } : {}),
              }
            : parsed.start;
        dataSource.cellValueChange(rowId, cell.property.id, next);
        onChange?.(next);
      }}
    />
  );
};
